import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '@/context/CartContext';

const WHATSAPP_NUMBER = '91XXXXXXXXXX';

function buildWhatsAppMessage(
  items: ReturnType<typeof useCart>['items'],
  customerInfo: ReturnType<typeof useCart>['customerInfo'],
  subtotal: number,
): string {
  const header = '🐾 *New Order from PounceBounce!*\n\n';

  let customerSection = '';
  if (customerInfo.name || customerInfo.phone) {
    customerSection = '*Customer Details:*\n';
    if (customerInfo.name) customerSection += `👤 Name: ${customerInfo.name}\n`;
    if (customerInfo.phone) customerSection += `📱 Phone: ${customerInfo.phone}\n`;
    if (customerInfo.address) customerSection += `📍 Address: ${customerInfo.address}\n`;
    if (customerInfo.city) customerSection += `🏙️ City: ${customerInfo.city}`;
    if (customerInfo.pincode) customerSection += ` - ${customerInfo.pincode}`;
    customerSection += '\n\n';
  }

  const itemsSection = `*Order Items:*\n${items
    .map((item, i) => {
      const price = item.variantPrice ?? item.product.price;
      const line = `${i + 1}. ${item.product.name}`;
      const variant = item.variantLabel ? ` (${item.variantLabel})` : '';
      return `${line}${variant} x${item.quantity} — Rs.${price * item.quantity}`;
    })
    .join('\n')}\n\n`;

  const totalSection = `*Subtotal: Rs.${subtotal}*\n\n`;

  let notesSection = '';
  if (customerInfo.orderNotes) {
    notesSection = `*Order Notes:*\n${customerInfo.orderNotes}\n\n`;
  }

  const footer = '_Please confirm my order! 🐾_';

  return header + customerSection + itemsSection + totalSection + notesSection + footer;
}

export default function CartPage() {
  const { items, customerInfo, subtotal, itemCount, updateQuantity, removeItem, setCustomerInfo, clearCart } = useCart();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formTouched, setFormTouched] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (field: string, value: string) => {
    setCustomerInfo({ [field]: value });
    if (!formTouched) setFormTouched(true);
    if (formErrors[field]) {
      setFormErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!customerInfo.name.trim()) errors.name = 'Name helps us know who to address';
    if (!customerInfo.phone.trim()) errors.phone = 'We need this to reach you on WhatsApp';
    else if (!/^[0-9+\-\s]{10,15}$/.test(customerInfo.phone.trim())) errors.phone = 'Enter a valid 10-digit phone number';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleWhatsAppCheckout = () => {
    setFormTouched(true);
    if (!validateForm()) return;

    const message = buildWhatsAppMessage(items, customerInfo, subtotal);
    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encoded}`, '_blank');
    setShowConfirmation(true);
  };

  // Empty cart state
  if (items.length === 0) {
    return (
      <main className="min-h-screen bg-background-50 pt-20">
        <div className="w-full px-4 md:px-8 lg:px-12 py-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-12" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium">Cart</span>
          </nav>

          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-24 h-24 rounded-full bg-secondary-100 flex items-center justify-center mb-6">
              <i className="ri-footprint-line text-4xl text-accent-500/60"></i>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 mb-3">Your Cart is Resting</h1>
            <p className="text-foreground-500 text-sm md:text-base mb-8 max-w-md leading-relaxed">
              Looks a little empty — pounce on something your pet will love! Browse our collection of non-toxic, chew-safe toys and accessories.
            </p>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Pounce on Something New
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </main>
    );
  }

  // Post-checkout confirmation banner
  if (showConfirmation) {
    return (
      <main className="min-h-screen bg-background-50 pt-20">
        <div className="w-full px-4 md:px-8 lg:px-12 py-8">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-12" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium">Cart</span>
          </nav>

          <div className="flex flex-col items-center justify-center py-20 text-center max-w-lg mx-auto">
            <div className="w-24 h-24 rounded-full bg-[#25D366]/10 flex items-center justify-center mb-6">
              <i className="ri-whatsapp-fill text-4xl text-[#25D366]"></i>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 mb-3">Order Summary Sent!</h1>
            <p className="text-foreground-500 text-sm md:text-base mb-2 leading-relaxed">
              Your order summary has been sent to WhatsApp — please tap <strong>send</strong> to confirm your order with us.
            </p>
            <p className="text-foreground-500 text-sm mb-8 leading-relaxed">
              We&rsquo;ll get back to you to confirm details and delivery. Typical response time: within 2 hours during business hours.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Continue Shopping
                <i className="ri-arrow-right-line"></i>
              </Link>
              <button
                onClick={() => { clearCart(); setShowConfirmation(false); }}
                className="inline-flex items-center gap-2 px-6 py-3 border border-background-300 text-foreground-600 rounded-full font-medium text-sm hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-delete-bin-line"></i>
                Clear Cart & Start Fresh
              </button>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background-50 pt-20">
      <div className="w-full px-4 md:px-8 lg:px-12 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
          <i className="ri-arrow-right-s-line text-xs"></i>
          <span className="text-foreground-700 font-medium">Cart</span>
        </nav>

        {/* Page header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 flex items-center gap-3">
            <i className="ri-shopping-bag-line text-primary-500"></i>
            Your Cart
            <span className="text-base font-normal text-foreground-500">
              ({itemCount} {itemCount === 1 ? 'item' : 'items'})
            </span>
          </h1>
          <button
            onClick={clearCart}
            className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-accent-500 transition-colors duration-200 cursor-pointer"
          >
            <i className="ri-delete-bin-line"></i>
            <span className="hidden sm:inline">Clear Cart</span>
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left: Cart Items Table */}
          <div className="flex-1 min-w-0">
            {/* Desktop table header */}
            <div className="hidden sm:grid grid-cols-12 gap-4 pb-3 border-b border-background-200/70 mb-1">
              <span className="col-span-5 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Product</span>
              <span className="col-span-2 text-xs font-semibold text-foreground-500 uppercase tracking-wider text-center">Price</span>
              <span className="col-span-2 text-xs font-semibold text-foreground-500 uppercase tracking-wider text-center">Quantity</span>
              <span className="col-span-2 text-xs font-semibold text-foreground-500 uppercase tracking-wider text-right">Total</span>
              <span className="col-span-1"></span>
            </div>

            {/* Cart items */}
            <div className="space-y-1">
              {items.map(item => {
                const price = item.variantPrice ?? item.product.price;
                const lineTotal = price * item.quantity;

                return (
                  <div
                    key={item.id}
                    className="group grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-4 items-center py-4 border-b border-background-200/50 hover:bg-background-50/60 transition-colors duration-150 px-0 sm:px-2 rounded-lg"
                  >
                    {/* Product info */}
                    <div className="col-span-1 sm:col-span-5 flex items-center gap-3 sm:gap-4 min-w-0">
                      <Link
                        to={`/product/${item.product.slug}`}
                        className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden flex-shrink-0 bg-secondary-100 block"
                      >
                        <img
                          src={item.product.image}
                          alt={item.product.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </Link>
                      <div className="min-w-0">
                        <Link
                          to={`/product/${item.product.slug}`}
                          className="text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors line-clamp-2 block"
                        >
                          {item.product.name}
                        </Link>
                        {item.variantLabel && (
                          <p className="text-xs text-foreground-500 mt-0.5">{item.variantLabel}</p>
                        )}
                        <p className="text-xs text-foreground-400 mt-0.5">{item.product.material}</p>
                        {/* Mobile price */}
                        <p className="sm:hidden text-sm font-bold text-foreground-800 mt-1.5">Rs.{lineTotal}</p>
                      </div>
                    </div>

                    {/* Price (desktop) */}
                    <div className="hidden sm:flex col-span-2 items-center justify-center">
                      <span className="text-sm text-foreground-700">Rs.{price}</span>
                    </div>

                    {/* Quantity stepper */}
                    <div className="col-span-1 sm:col-span-2 flex items-center justify-start sm:justify-center gap-0">
                      <div className="flex items-center bg-background-100 rounded-full p-1">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-7 h-7 flex items-center justify-center rounded-full bg-background-50 text-foreground-600 hover:text-accent-500 hover:bg-background-50 transition-colors duration-150 text-xs border border-background-200 cursor-pointer"
                          aria-label={`Decrease quantity of ${item.product.name}`}
                        >
                          <i className="ri-subtract-line"></i>
                        </button>
                        <span className="w-8 text-center text-sm font-medium text-foreground-800 select-none">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-7 h-7 flex items-center justify-center rounded-full bg-background-50 text-foreground-600 hover:text-primary-500 hover:bg-background-50 transition-colors duration-150 text-xs border border-background-200 cursor-pointer"
                          aria-label={`Increase quantity of ${item.product.name}`}
                        >
                          <i className="ri-add-line"></i>
                        </button>
                      </div>
                    </div>

                    {/* Line total (desktop) */}
                    <div className="hidden sm:flex col-span-2 items-center justify-end">
                      <span className="text-sm font-bold text-foreground-800">Rs.{lineTotal}</span>
                    </div>

                    {/* Remove button */}
                    <div className="col-span-1 flex items-center justify-end">
                      <button
                        onClick={() => removeItem(item.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-accent-500 hover:bg-accent-50 transition-colors duration-150 cursor-pointer"
                        aria-label={`Remove ${item.product.name} from cart`}
                      >
                        <i className="ri-close-line text-lg"></i>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Continue shopping link */}
            <div className="mt-6">
              <Link
                to="/shop"
                className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-200"
              >
                <i className="ri-arrow-left-line"></i>
                Continue Shopping
              </Link>
            </div>
          </div>

          {/* Right: Order Summary & Checkout */}
          <div className="w-full lg:w-[380px] flex-shrink-0">
            <div className="bg-background-100/60 rounded-xl p-6 lg:sticky lg:top-24 space-y-6">
              <h2 className="font-heading text-lg font-bold text-foreground-800">Order Summary</h2>

              {/* Subtotal */}
              <div className="flex items-center justify-between pb-4 border-b border-background-200/70">
                <span className="text-sm text-foreground-600">Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'})</span>
                <span className="text-xl font-bold text-foreground-800">Rs.{subtotal}</span>
              </div>

              {/* Delivery note */}
              <div className="flex items-start gap-2 p-3 rounded-lg bg-secondary-50 border border-secondary-200/50">
                <i className="ri-information-line text-secondary-500 text-sm mt-0.5 flex-shrink-0"></i>
                <p className="text-xs text-foreground-600 leading-relaxed">
                  Final amount including any delivery charges will be confirmed over WhatsApp. No payment is collected on this website.
                </p>
              </div>

              {/* Customer Info Form */}
              <div className="space-y-4">
                <h3 className="font-heading text-sm font-bold text-foreground-700 flex items-center gap-2">
                  <i className="ri-user-settings-line text-primary-500"></i>
                  Your Details
                  <span className="text-xs font-normal text-foreground-400">(required for WhatsApp order)</span>
                </h3>

                {/* Name */}
                <div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name *"
                    value={customerInfo.name}
                    onChange={e => handleInputChange('name', e.target.value)}
                    className={`w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border ${
                      formTouched && formErrors.name ? 'border-accent-500' : 'border-background-200'
                    } text-foreground-800 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors`}
                    autoComplete="name"
                  />
                  {formTouched && formErrors.name && (
                    <p className="text-xs text-accent-500 mt-1 flex items-center gap-1">
                      <i className="ri-error-warning-line"></i> {formErrors.name}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number *"
                    value={customerInfo.phone}
                    onChange={e => handleInputChange('phone', e.target.value)}
                    className={`w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border ${
                      formTouched && formErrors.phone ? 'border-accent-500' : 'border-background-200'
                    } text-foreground-800 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors`}
                    autoComplete="tel"
                  />
                  {formTouched && formErrors.phone && (
                    <p className="text-xs text-accent-500 mt-1 flex items-center gap-1">
                      <i className="ri-error-warning-line"></i> {formErrors.phone}
                    </p>
                  )}
                </div>

                {/* Address */}
                <div>
                  <input
                    type="text"
                    name="address"
                    placeholder="Delivery Address"
                    value={customerInfo.address}
                    onChange={e => handleInputChange('address', e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-200 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                    autoComplete="street-address"
                  />
                </div>

                {/* City + Pincode row */}
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    name="city"
                    placeholder="City"
                    value={customerInfo.city}
                    onChange={e => handleInputChange('city', e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-200 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                    autoComplete="address-level2"
                  />
                  <input
                    type="text"
                    name="pincode"
                    placeholder="Pincode"
                    value={customerInfo.pincode}
                    onChange={e => handleInputChange('pincode', e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-200 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                    autoComplete="postal-code"
                  />
                </div>

                {/* Order Notes */}
                <div>
                  <textarea
                    name="orderNotes"
                    placeholder="Order Notes (e.g., gifting instructions, delivery preferences)"
                    value={customerInfo.orderNotes}
                    onChange={e => handleInputChange('orderNotes', e.target.value)}
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-200 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors resize-none"
                  />
                  <p className="text-xs text-foreground-400 mt-1 text-right">
                    {customerInfo.orderNotes.length}/500
                  </p>
                </div>

                {/* Anti-spam honeypot */}
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="opacity-0 absolute top-0 left-0 h-0 w-0 -z-10 pointer-events-none"
                />

                {/* WhatsApp Checkout CTA */}
                <button
                  onClick={handleWhatsAppCheckout}
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full font-semibold text-sm transition-all duration-200 bg-[#25D366] text-white hover:bg-[#22c35e] cursor-pointer group"
                >
                  <i className="ri-whatsapp-fill text-lg transition-transform duration-300 group-hover:scale-110"></i>
                  Send Order via WhatsApp
                </button>

                <p className="text-xs text-foreground-400 text-center">
                  Opens WhatsApp — tap send to confirm your order with us
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}